import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './_styles_/Zones.css';
import Paginator from '../Paginator';
import { parseIncludeDeactivated, parsePaging, parseSorting } from '../../utils/queryString';
import Spinner from '../Spinner';
import useExtendedQuery from '../../hooks/useExtendedQuery';
import { ALL_ZONES, ON_ZONE_CHANGE } from '../../graphql/zones';
import ZonesList from './ZonesList';
import useExtendedSubscription from '../../hooks/useExtendedSubscription';
import { showInfoNotification } from '../../actions/notifications';
import SubscriptionActions from '../../utils/SubscriptionActions';
import CreateZoneRow from './CreateZoneRow';
import Constants from '../../utils/Constants';
import Error from '../errors/Error';
import CheckboxField from '../fields/CheckboxField';
import { getValueMapping } from './ZoneRow';
import useAuthentication from '../../hooks/useAuthentication';
import { hasPrivilege } from '../../hocs/withPrivileges';
import Privileges from '../../utils/Privileges';

export function createNotificationText(t, action, name, code) {
    let notification;
    switch (action) {
        case SubscriptionActions.ZONE_CREATED:
            notification = t('zones.notification.zone-create-thirdparty', { name, code });
            break;
        case SubscriptionActions.ZONE_UPDATED:
            notification = t('zones.notification.zone-update-thirdparty', { name, code });
            break;
        default:
            notification = t('zones.notification.zones-modified-thirdparty');
    }
    return notification;
}

function Zones() {
    const location = useLocation();
    const { t } = useTranslation();
    const { userPrivileges } = useAuthentication();
    const { page, perPage } = parsePaging(location.search);
    const {
        sortKey = 'code',
        sortDirection = Constants.SORT_ASC,
    } = parseSorting(location.search);
    const includeDeactivated = parseIncludeDeactivated(location.search);
    const dispatch = useDispatch();
    const [refetchProps, setRefetchProps] = useState({
        perPage,
        page,
        includeDeactivated,
        sortKey,
        sortDirection,
    });
    const {
        loading,
        error,
        data,
        refetch,
    } = useExtendedQuery(ALL_ZONES, {
        variables: refetchProps,
        valueMappingFn: getValueMapping,
    });
    const onPageChange = (newPage) => setRefetchProps({
        ...refetchProps,
        page: newPage,
    });
    const onSort = (newSorting) => setRefetchProps({
        ...refetchProps,
        sortKey: newSorting.key,
        sortDirection: newSorting.direction,
    });
    const onFilter = (newIncludeDeactivated) => setRefetchProps({
        ...refetchProps,
        includeDeactivated: newIncludeDeactivated,
        page: 0,
    });
    useExtendedSubscription(ON_ZONE_CHANGE, {
        refetch,
        refetchVariables: refetchProps,
        dataProperty: 'zoneChanged',
        onChangeCallback: ({ action, model: { name, code } }) => {
            if (hasPrivilege(userPrivileges, [Privileges.ZONE_EDIT_ALL])) {
                const notification = createNotificationText(t, action, name, code);
                dispatch(showInfoNotification(notification));
            }
        },
    });
    let result;
    let zoneProps = null;
    if (loading) {
        result = <Spinner className="zones-loading" />;
    } else if (error) {
        result = <Error className="zones-error" error={error} />;
    } else {
        zoneProps = {
            availableCurrencies: data.allZones.availableCurrencies,
            availableClearingHouses: data.allZones.availableClearingHouses,
        };
        result = (
            <Paginator
                totalCount={data.allZones.zoneList.totalCount}
                perPage={perPage}
                currentPage={refetchProps.page}
                onPageChange={onPageChange}
                bottom
            >
                <ZonesList
                    zones={data.allZones.zoneList.models}
                    onSort={onSort}
                    sortingParam={{ key: refetchProps.sortKey, direction: refetchProps.sortDirection }}
                    zoneProps={zoneProps}
                />
            </Paginator>
        );
    }
    return (
        <>
            <h4><FontAwesomeIcon icon="map-marked-alt" />&nbsp;{t('title.zones')}</h4>
            {zoneProps && <CreateZoneRow zoneProps={zoneProps} />}
            <div className="search-and-filter zones">
                <div className="filter">
                    <CheckboxField
                        label={t('zones.include-deactivated')}
                        value={refetchProps.includeDeactivated}
                        onChange={onFilter}
                        className="zones--search-and-filter--include-deactivated"
                        inputId="zones--search-and-filter--include-deactivated"
                    />
                </div>
            </div>
            {result}
        </>
    );
}

export default Zones;
